import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { CalendarDays, Check, ChevronDown, User, X } from 'lucide-react';
import { format, startOfMonth, startOfWeek, subDays } from 'date-fns';
import { cn } from '@/lib/utils';
import { useApi, type Paginated } from '@/services/query';
import type { LookupEmployee } from '@/types';
import { Avatar, Input } from './primitives';
import { FilterOption, FilterPopover, FilterTrigger, PopoverSearch, useInFilterBar, useOutsideClose } from './filters';

interface Option {
  id: string;
  label: string;
  sub?: string | null;
}

/**
 * Searchable single/multi select backed by an API endpoint.
 * `toOption` maps each row; `listKey` handles paginated (`items`) or plain array responses.
 */
export function AsyncSelect({
  url,
  params,
  value,
  onChange,
  multiple,
  placeholder = 'Select…',
  toOption,
  disabled,
  invalid,
  id,
  selectedLabels,
  icon,
  compact,
}: {
  url: string;
  params?: Record<string, unknown>;
  value: string[] | string | null | undefined;
  onChange: (v: string[] | string | null) => void;
  multiple?: boolean;
  placeholder?: string;
  toOption: (row: never) => Option;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  /** Labels for already-selected ids that may not be in the current search results. */
  selectedLabels?: Record<string, string>;
  icon?: ReactNode;
  /** Compact filter trigger; defaults to true inside a FilterBar. */
  compact?: boolean;
}) {
  const inFilterBar = useInFilterBar();
  const isCompact = compact ?? inFilterBar;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const query = useApi<unknown>(url, { ...params, q: debounced || undefined }, { enabled: open || !!value });
  const options = useMemo(() => {
    const d = query.data as Paginated<unknown> | unknown[] | undefined;
    const rows = Array.isArray(d) ? d : (d?.items ?? []);
    return rows.map((r) => toOption(r as never));
  }, [query.data, toOption]);

  const [known, setKnown] = useState<Record<string, string>>({});
  useEffect(() => {
    if (options.length) setKnown((k) => ({ ...k, ...Object.fromEntries(options.map((o) => [o.id, o.label])) }));
  }, [options]);

  const selected = (multiple ? ((value as string[]) ?? []) : value ? [value as string] : []).filter(Boolean);
  const label = (sid: string) => known[sid] ?? selectedLabels?.[sid] ?? '…';
  const toggle = (oid: string) => {
    if (multiple) onChange(selected.includes(oid) ? selected.filter((x) => x !== oid) : [...selected, oid]);
    else {
      onChange(selected[0] === oid ? null : oid);
      setOpen(false);
    }
  };

  const list = (
    <ul role="listbox" aria-multiselectable={multiple || undefined} className="scrollbar-thin max-h-60 overflow-y-auto">
      {query.isLoading && <li className="px-3 py-2 text-xs text-muted">Loading…</li>}
      {!query.isLoading && options.length === 0 && <li className="px-3 py-2 text-xs text-muted">No matches</li>}
      {options.map((o) => (
        <li key={o.id}>
          <FilterOption multi={multiple} selected={selected.includes(o.id)} onClick={() => toggle(o.id)}>
            <span className="block truncate">{o.label}</span>
            {o.sub && <span className="block truncate text-[11px] font-normal text-muted">{o.sub}</span>}
          </FilterOption>
        </li>
      ))}
    </ul>
  );

  if (isCompact) {
    const summary = selected.length === 0 ? undefined : selected.length === 1 ? label(selected[0]) : `${label(selected[0])} +${selected.length - 1}`;
    const m = /^(?:any|no|select)\s+(.+?)[….]*$/i.exec(placeholder);
    const ph = m ? `By ${m[1].charAt(0).toUpperCase()}${m[1].slice(1)}` : placeholder;
    return (
      <div ref={ref} className="relative w-full sm:w-auto">
        <FilterTrigger id={id} open={open} disabled={disabled} placeholder={ph} valueLabel={summary} icon={icon} onClick={() => setOpen((o) => !o)} onClear={() => onChange(multiple ? [] : null)} />
        {open && (
          <FilterPopover className="w-64">
            <PopoverSearch value={q} onChange={setQ} />
            {list}
          </FilterPopover>
        )}
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        id={id}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={invalid || undefined}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex min-h-10 w-full items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-left text-sm transition-colors focus:border-muted focus:outline-none disabled:opacity-60',
          invalid && 'border-danger',
        )}
      >
        <div className="flex min-w-0 flex-1 flex-wrap gap-1">
          {selected.length === 0 && <span className="text-subtle">{placeholder}</span>}
          {multiple
            ? selected.map((sid) => (
                <span key={sid} className="inline-flex items-center gap-1 rounded-md bg-primary-soft px-2 py-0.5 text-xs text-primary">
                  {label(sid)}
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={`Remove ${label(sid)}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(sid);
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && toggle(sid)}
                  >
                    <X className="h-3 w-3" />
                  </span>
                </span>
              ))
            : selected[0] && <span className="truncate">{label(selected[0])}</span>}
        </div>
        {!multiple && selected[0] && !disabled && (
          <span
            role="button"
            tabIndex={0}
            aria-label="Clear selection"
            className="text-subtle hover:text-text"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
          >
            <X className="h-3.5 w-3.5" />
          </span>
        )}
        <ChevronDown className="h-4 w-4 shrink-0 text-subtle" aria-hidden />
      </button>
      {open && (
        <div className="absolute z-40 mt-1 w-full min-w-[240px] rounded-xl border border-border bg-surface p-1 shadow-xl animate-scale-in">
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type to search…" className="mb-1 h-9" aria-label="Search options" />
          <ul role="listbox" aria-multiselectable={multiple || undefined} className="scrollbar-thin max-h-60 overflow-y-auto">
            {query.isLoading && <li className="px-3 py-2 text-xs text-muted">Loading…</li>}
            {!query.isLoading && options.length === 0 && <li className="px-3 py-2 text-xs text-muted">No matches</li>}
            {options.map((o) => {
              const on = selected.includes(o.id);
              return (
                <li key={o.id} role="option" aria-selected={on}>
                  <button type="button" onClick={() => toggle(o.id)} className={cn('flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-surface-2', on && 'bg-primary-soft')}>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{o.label}</span>
                      {o.sub && <span className="block truncate text-xs text-muted">{o.sub}</span>}
                    </span>
                    {on && <Check className="h-4 w-4 text-primary" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Employee picker over /employees/lookup. `assignable` limits to people the user may assign work to. */
export function EmployeePicker(props: {
  value: string[] | string | null | undefined;
  onChange: (v: string[] | string | null) => void;
  multiple?: boolean;
  assignable?: boolean;
  teamId?: string;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  selectedLabels?: Record<string, string>;
  compact?: boolean;
}) {
  const ids = Array.isArray(props.value) ? props.value : props.value ? [props.value] : [];
  const prefetch = useApi<LookupEmployee[]>(ids.length ? '/employees/lookup' : null, { ids: ids.join(','), includeInactive: 'true', includeDeleted: 'true', limit: 200 });
  const labels = useMemo(() => ({ ...props.selectedLabels, ...Object.fromEntries((prefetch.data ?? []).map((e) => [e.id, e.fullName])) }), [prefetch.data, props.selectedLabels]);
  return (
    <AsyncSelect
      {...props}
      icon={<User className="h-4 w-4" aria-hidden />}
      url="/employees/lookup"
      params={{ assignable: props.assignable ? 'true' : undefined, teamId: props.teamId, limit: 50 }}
      selectedLabels={labels}
      placeholder={props.placeholder ?? (props.multiple ? 'Select employees…' : 'Select employee…')}
      toOption={(e: LookupEmployee) => ({ id: e.id, label: e.fullName, sub: [e.employeeCode, e.designation, e.department].filter(Boolean).join(' · ') })}
    />
  );
}

export function EmployeeChip({ name, sub }: { name: string; sub?: string | null }) {
  return (
    <span className="inline-flex min-w-0 items-center gap-2">
      <Avatar name={name} size={26} />
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium">{name}</span>
        {sub && <span className="block truncate text-xs text-muted">{sub}</span>}
      </span>
    </span>
  );
}

export type RangePreset = 'today' | 'week' | 'month' | '30d' | 'custom';

/** Date range filter with Today / This Week / This Month / Last 30 days / custom. Values are ISO dates. */
export function DateRangeFilter({
  from,
  to,
  onChange,
  presets = ['today', 'week', 'month', '30d', 'custom'],
  label = 'Date',
}: {
  from?: string;
  to?: string;
  onChange: (r: { from?: string; to?: string }) => void;
  presets?: RangePreset[];
  label?: string;
}) {
  const today = new Date();
  const iso = (d: Date) => format(d, 'yyyy-MM-dd');
  const ranges: Record<Exclude<RangePreset, 'custom'>, { from: string; to: string; label: string }> = {
    today: { from: iso(today), to: iso(today), label: 'Today' },
    week: { from: iso(startOfWeek(today, { weekStartsOn: 1 })), to: iso(today), label: 'This week' },
    month: { from: iso(startOfMonth(today)), to: iso(today), label: 'This month' },
    '30d': { from: iso(subDays(today, 29)), to: iso(today), label: 'Last 30 days' },
  };
  const current = (Object.entries(ranges).find(([, r]) => r.from === from && r.to === to)?.[0] as RangePreset | undefined) ?? (from || to ? 'custom' : undefined);
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(current === 'custom');
  const ref = useOutsideClose(open, () => setOpen(false));
  const short = (d?: string) => (d ? format(new Date(`${d}T00:00:00`), 'dd MMM') : '…');
  const valueLabel = !current ? undefined : current === 'custom' ? `${short(from)} – ${short(to)}` : ranges[current].label;
  return (
    <div ref={ref} className="relative w-full sm:w-auto">
      <FilterTrigger
        open={open}
        placeholder={`By ${label}`}
        valueLabel={valueLabel}
        icon={<CalendarDays className="h-4 w-4" aria-hidden />}
        ariaLabel={`${label} range filter`}
        onClick={() => setOpen((o) => !o)}
        onClear={() => {
          setCustom(false);
          onChange({ from: undefined, to: undefined });
        }}
      />
      {open && (
        <FilterPopover className="w-60">
          <div role="listbox" aria-label={`${label} range`}>
            {presets
              .filter((p) => p !== 'custom')
              .map((p) => {
                const r = ranges[p as Exclude<RangePreset, 'custom'>];
                return (
                  <FilterOption
                    key={p}
                    selected={current === p}
                    onClick={() => {
                      setCustom(false);
                      onChange({ from: r.from, to: r.to });
                      setOpen(false);
                    }}
                  >
                    {r.label}
                  </FilterOption>
                );
              })}
            {presets.includes('custom') && (
              <FilterOption selected={custom || current === 'custom'} onClick={() => setCustom(true)}>
                Custom range…
              </FilterOption>
            )}
          </div>
          {(custom || current === 'custom') && (
            <div className="mt-1 grid gap-2 border-t border-border p-2">
              <label className="grid gap-1 text-[11px] text-muted">
                From
                <Input type="date" className="h-8 text-xs" value={from ?? ''} max={to} onChange={(e) => onChange({ from: e.target.value || undefined, to })} />
              </label>
              <label className="grid gap-1 text-[11px] text-muted">
                To
                <Input type="date" className="h-8 text-xs" value={to ?? ''} min={from} onChange={(e) => onChange({ from, to: e.target.value || undefined })} />
              </label>
            </div>
          )}
        </FilterPopover>
      )}
    </div>
  );
}
